import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type ForumAccessJSON = z.infer<typeof ForumAccessSchema.ForumAccessJSONZ>;
export type ForumAccessParams = z.infer<typeof ForumAccessSchema.ForumAccessZ>;

export default class ForumAccessSchema {
  public static readonly ForumAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.ForumAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    forumId: z.uuid().nullish(),
  });

  public static readonly ForumAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.ForumAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    forumId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): ForumAccessParams {
    return parseOrThrow(ForumAccessSchema.ForumAccessZ, values, {
      logError: true,
      schemaName: "ForumAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): ForumAccessJSON {
    return parseOrThrow(ForumAccessSchema.ForumAccessJSONZ, values, {
      logError: true,
      schemaName: "ForumAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ForumAccessParams {
    try {
      ForumAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is ForumAccessJSON {
    try {
      ForumAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: ForumAccessJSON): ForumAccessParams {
    const parsed = parseOrThrow(ForumAccessSchema.ForumAccessJSONZ, data, {
      logError: true,
      schemaName: "ForumAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return ForumAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: ForumAccessParams): ForumAccessJSON {
    const parsed = ForumAccessSchema.validate(notification);
    return ForumAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(ForumAccessSchema.ForumAccessJSONZ, ForumAccessSchema.ForumAccessZ, {
    decode: (data) => {
      return {
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.ForumAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        forumId: Guid.fromJSON(data.forumId) ?? undefined,
      };
    },
    encode: (resource) => ({
      ...PermissionResourceSchema.serialize(resource),
      resourceType: ResourceType.ForumAccess as const,
      accessLevel: resource.accessLevel,
      forumId: resource.forumId?.toJSON(),
    }),
  });
}
